import React, { useState, useEffect } from "react";
import apiClient from "../../../api/apiClient";
import Loading from "../../../components/Loading/Loading";
import { useToast } from "../../../context/toastContext";
import { useConfirm } from "../../../context/confirmContext";
import PartnerSummary from "../../../components/SupplyChainPartners/PartnerSummary";

const formatPhone = (phone) => {
    if (!phone) return "—";
    if (phone.startsWith("+1") && phone.length === 12) {
        return `+1 (${phone.slice(2, 5)}) ${phone.slice(5, 8)}-${phone.slice(8)}`;
    }
    return phone; 
};

const EMPTY_EDIT_FORM = { firstName: "", lastName: "", email: "", phoneNumber: "", license: null };

const PendingApprovals = () => {
    const toast = useToast();
    const confirm = useConfirm();
    const [pending, setPending] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingId, setEditingId] = useState(null);
    const [editForm, setEditForm] = useState(EMPTY_EDIT_FORM);
    const [editSubmitting, setEditSubmitting] = useState(false);
    // Admin-confirmed CRM name matches per pending license: { [licenseNo]: leadId[] }.
    // Identifier (USDA #/EIN) matches link automatically on approval and need no confirmation.
    const [leadMatches, setLeadMatches] = useState({});

    const fetchPending = async () => {
        setLoading(true);
        try {
            const res = await apiClient.get("/admin/pending-approvals");
            setPending(res.data);
        } catch (err) { 
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchPending(); }, []);

    const handleApprove = async (user) => {
        const ok = await confirm({
            title: "Accept Registration",
            message: `Accept registration for ${user.firstName} ${user.lastName}?`,
            confirmLabel: "Accept",
            variant: "primary",
        });
        if (!ok) return;
        setLoading(true);
        try {
            // Preview-only fields are not sent back; approval re-reads the supply chain server-side.
            const licenses = user.licenses.map(({ partners, leadSuggestions, supplyChainWarning, ...l }) => l);
            const matches = Object.fromEntries(
                user.licenses.map((l) => [l.licenseNo, leadMatches[l.licenseNo] || []]).filter(([, ids]) => ids.length)
            );
            const res = await apiClient.post("/register/AcceptUserAccount", { user: { ...user, licenses }, leadMatches: matches });
            toast.success(`${user.firstName} ${user.lastName} approved.`);
            for (const warning of res.data?.supplyChainWarnings || []) toast.error(`Supply chain incomplete — ${warning}`);
            fetchPending();
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to accept registration.");
            setLoading(false);
        }
    };

    const handleDeny = async (user) => {
        const ok = await confirm({
            title: "Deny Registration",
            message: `Deny registration for ${user.firstName} ${user.lastName}? This cannot be undone.`,
            confirmLabel: "Deny",
            variant: "danger",
        });
        if (!ok) return;
        setLoading(true);
        try {
            await apiClient.put(`/admin/pending-approvals/${user.id}/deny`);
            toast.success(`${user.firstName} ${user.lastName} denied.`);
            fetchPending();
        } catch (err) {
            toast.error(err.response?.data?.error || "Failed to deny registration.");
            setLoading(false);
        }
    };

    const startEdit = (user) => {
        setEditingId(user.id);
        setEditForm({
            firstName: user.firstName || "",
            lastName: user.lastName || "",
            email: user.email || "",
            phoneNumber: user.phoneNumber || "",
            license: user.licenses?.[0]
                ? {
                    licenseNo: user.licenses[0].licenseNo,
                    company: user.licenses[0].company || "",
                    address: user.licenses[0].address || "",
                    city: user.licenses[0].city || "",
                    state: user.licenses[0].state || "",
                    zipCode: user.licenses[0].zipCode || "",
                }
                : null,
        });
    };

    const handleSaveEdit = async (id) => {
        setEditSubmitting(true);
        try {
            const { license, ...userFields } = editForm;
            const body = { ...userFields };
            if (license) body.licenses = [license];
            await apiClient.put(`/admin/pending-approvals/${id}`, body);
            toast.success("Registration updated.");
            setEditingId(null);
            fetchPending();
        } catch (err) {
            toast.error(err.response?.data?.error || "Failed to update registration");
        } finally {
            setEditSubmitting(false);
        }
    };

    if (loading) return <div className="admin-loading-wrapper"><Loading /></div>;

    return (
        <div className="admin-pending-list">
            {pending.length === 0 && (
                <p className="admin-empty">No pending registrations.</p>
            )}
            {pending.map((user) => (
                <div key={user.id} className="admin-pending-card">
                    <div className="admin-pending-header">
                        <div>
                            <div className="admin-pending-name">{user.firstName} {user.lastName}</div>
                            <div className="admin-pending-meta">{user.email} · {formatPhone(user.phoneNumber)}</div>
                            <div className="admin-pending-meta">Role: <strong>{user.title}</strong></div>
                        </div>
                        <div className="admin-pending-actions">
                            <button className="admin-action-btn enable" onClick={() => handleApprove(user)}>
                                Accept
                            </button>
                            <button className="admin-action-btn" onClick={() => startEdit(user)}>
                                Edit
                            </button>
                            <button className="admin-action-btn disable" onClick={() => handleDeny(user)}>
                                Deny
                            </button>
                        </div>
                    </div>

                    {editingId === user.id && (
                        <div className="admin-pending-edit-form">
                            <div className="admin-form-row">
                                <div className="admin-form-group">
                                    <label className="admin-form-label">First Name</label>
                                    <input className="admin-form-input" value={editForm.firstName} onChange={(e) => setEditForm((p) => ({ ...p, firstName: e.target.value }))} />
                                </div>
                                <div className="admin-form-group">
                                    <label className="admin-form-label">Last Name</label>
                                    <input className="admin-form-input" value={editForm.lastName} onChange={(e) => setEditForm((p) => ({ ...p, lastName: e.target.value }))} />
                                </div>
                                <div className="admin-form-group">
                                    <label className="admin-form-label">Email</label>
                                    <input className="admin-form-input" value={editForm.email} onChange={(e) => setEditForm((p) => ({ ...p, email: e.target.value }))} />
                                </div>
                                <div className="admin-form-group">
                                    <label className="admin-form-label">Phone</label>
                                    <input className="admin-form-input" value={editForm.phoneNumber} onChange={(e) => setEditForm((p) => ({ ...p, phoneNumber: e.target.value }))} />
                                </div>
                            </div>
                            {editForm.license && (
                                <div className="admin-form-row">
                                    <div className="admin-form-group">
                                        <label className="admin-form-label">Company</label>
                                        <input className="admin-form-input" value={editForm.license.company} onChange={(e) => setEditForm((p) => ({ ...p, license: { ...p.license, company: e.target.value } }))} />
                                    </div>
                                    <div className="admin-form-group">
                                        <label className="admin-form-label">Address</label>
                                        <input className="admin-form-input" value={editForm.license.address} onChange={(e) => setEditForm((p) => ({ ...p, license: { ...p.license, address: e.target.value } }))} />
                                    </div>
                                    <div className="admin-form-group">
                                        <label className="admin-form-label">City</label>
                                        <input className="admin-form-input" value={editForm.license.city} onChange={(e) => setEditForm((p) => ({ ...p, license: { ...p.license, city: e.target.value } }))} />
                                    </div>
                                    <div className="admin-form-group">
                                        <label className="admin-form-label">State</label>
                                        <input className="admin-form-input" maxLength={2} value={editForm.license.state} onChange={(e) => setEditForm((p) => ({ ...p, license: { ...p.license, state: e.target.value.toUpperCase() } }))} />
                                    </div>
                                    <div className="admin-form-group">
                                        <label className="admin-form-label">Zip</label>
                                        <input className="admin-form-input" value={editForm.license.zipCode} onChange={(e) => setEditForm((p) => ({ ...p, license: { ...p.license, zipCode: e.target.value } }))} />
                                    </div>
                                </div>
                            )}
                            <div style={{ display: "flex", gap: "var(--space-3)", marginTop: "var(--space-3)" }}>
                                <button className="admin-action-btn primary" disabled={editSubmitting} onClick={() => handleSaveEdit(user.id)}>
                                    {editSubmitting ? "Saving…" : "Save"}
                                </button>
                                <button className="admin-action-btn" onClick={() => setEditingId(null)}>Cancel</button>
                            </div>
                        </div>
                    )}
                    {user.licenses?.length > 0 && (
                        <div className="admin-pending-licenses">
                            {user.licenses.map((l, i) => (
                                <div key={`${user.id}-${l.licenseNo}-${i}`} className="admin-pending-license-row">
                                    <div className="admin-pending-license-info">
                                        <span style={{ fontWeight: 500 }}>{l.company}</span>
                                        <span className="admin-license-meta">{l.licenseNo} · {l.industry} · {l.profile}</span>
                                        <span className="admin-license-meta">{[l.address, l.city, l.state, l.zipCode].filter(Boolean).join(", ")}</span>
                                        {/* Read-only preview of what the registrant submitted — approval itself re-reads
                                            the supply chain server-side, never this data. */}
                                        <PartnerSummary profile={l.profile} partners={l.partners || []} operationType={l.operationType} />
                                        {l.supplyChainWarning && (
                                            <span className="admin-role-tag">Supply chain incomplete: {l.supplyChainWarning}</span>
                                        )}
                                        <LeadMatches
                                            licenseNo={l.licenseNo}
                                            suggestions={l.leadSuggestions}
                                            selected={leadMatches[l.licenseNo] || []}
                                            onChange={(ids) => setLeadMatches((prev) => ({ ...prev, [l.licenseNo]: ids }))}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
};

// CRM leads that may BE this registering business. USDA/EIN matches link automatically on
// approval; name matches are linked only when the admin ticks them.
function LeadMatches({ licenseNo, suggestions, selected, onChange }) {
    const auto = suggestions?.autoLink || [];
    const confirm = suggestions?.confirm || [];
    if (!auto.length && !confirm.length) return null;
    const toggle = (id) => onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
    return (
        <fieldset className="admin-pending-lead-matches">
            <legend className="admin-license-meta" style={{ display: "block", fontWeight: 600 }}>
                Possible CRM lead matches
            </legend>
            {auto.map((lead) => (
                <div key={lead.id} className="admin-license-meta" style={{ display: "block" }}>
                    ✓ {lead.company} — same USDA # / EIN, will be linked automatically (reported by {lead.activeCitationCount + lead.unverifiedCitationCount})
                </div>
            ))}
            {confirm.map((lead) => (
                <label key={lead.id} className="admin-license-meta" style={{ display: "flex", gap: "var(--space-2)", alignItems: "center" }}>
                    <input
                        type="checkbox"
                        checked={selected.includes(lead.id)}
                        onChange={() => toggle(lead.id)}
                        aria-describedby={`lead-${licenseNo}-${lead.id}-hint`}
                    />
                    <span>
                        {lead.company}
                        {lead.city || lead.state ? ` (${[lead.city, lead.state].filter(Boolean).join(", ")})` : ""}
                        <span id={`lead-${licenseNo}-${lead.id}-hint`}> — similar name; link on approval</span>
                    </span>
                </label>
            ))}
        </fieldset>
    );
}

export default PendingApprovals;
